// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The two artefacts.
 *
 * Both carry their own caveats. A report that leaves the tool stops being read
 * next to the interface that explained it — it gets forwarded to a supervisor,
 * pasted into an email, attached to a submission — so the sentence about what
 * "exact" means and the sentence about what NOT FOUND does not mean have to
 * travel inside the file, not beside it.
 */

import type { DraftQuote, ExtractedSource, IdentifierResult, QuoteResult } from '../types.ts';
import {
  CLASS_LABELS,
  EXACTNESS_CAVEAT,
  IDENTIFIER_RATE_LIMITED_NOTE,
  IDENTIFIER_TABLE_CAVEAT,
  IDENTIFIER_UNREACHABLE_CAVEAT,
  IDENTIFIER_UNREACHABLE_NOTE,
  IDENTIFIER_VERDICT_LABELS,
  NOT_FOUND_CAVEAT,
  sourceNoteLines,
} from '../content.ts';

// Excel decides a CSV's encoding on double-click, and without a byte-order mark
// it picks a legacy single-byte code page: every curly quotation mark, em dash
// and accented author name arrives as mojibake. This file is full of all three
// by construction — the NOT FOUND caveat alone carries an em dash, so even an
// all-ASCII draft with no quotations exported corrupted. Every other reader
// (Numbers, Sheets, LibreOffice, any text editor) sniffs UTF-8 and is unharmed
// by the mark. Written as an escape because a literal U+FEFF is invisible in an
// editor and in a diff, which is exactly how one gets deleted by accident.
const UTF8_BOM = '\uFEFF';

function csvCell(value: string | number | boolean | null): string {
  if (value === null) return '';
  const text = String(value);
  // A leading =, +, - or @ makes a spreadsheet treat the cell as a formula, and
  // this file is full of user text that a spreadsheet will happily execute.
  const guarded = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

export function buildCsv(
  quotes: DraftQuote[],
  results: QuoteResult[],
  sources: ExtractedSource[],
): string {
  const byId = new Map(sources.map((s) => [s.id, s]));
  const header = [
    'quote_id', 'verdict', 'spelling_only', 'source_file', 'pdf_sheet', 'printed_page',
    'page_number_from', 'cited_pinpoint', 'pinpoint_mismatch', 'quote_words', 'word_edits',
    'draft_offset', 'quotation', 'matched_text_as_extracted', 'note',
  ];
  const rows: string[] = [header.join(',')];

  for (const result of results) {
    const quote = quotes[result.quoteId];
    const source = result.sourceId === null ? null : byId.get(result.sourceId) ?? null;
    const page = source?.pages.find((p) => p.sheet === result.sheet) ?? null;
    rows.push(
      [
        result.quoteId + 1,
        CLASS_LABELS[result.cls],
        result.orthographic,
        source?.name ?? null,
        result.sheet,
        result.printedPage,
        page?.from ?? null,
        quote?.pinpoint?.raw ?? null,
        result.pinpointMismatch,
        result.quoteWords,
        result.wordEdits,
        quote?.start ?? null,
        quote?.text ?? null,
        result.context?.matched ?? null,
        result.skipped,
      ]
        .map(csvCell)
        .join(','),
    );
  }

  rows.push('');
  rows.push(csvCell(`# ${EXACTNESS_CAVEAT}`));
  rows.push(csvCell(`# ${NOT_FOUND_CAVEAT}`));
  return UTF8_BOM + rows.join('\r\n');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const REPORT_CSS = `
:root { color-scheme: light; }
* { box-sizing: border-box; }
body { margin: 0; padding: 2.5rem 1.25rem 4rem; background: #fdfcfa; color: #1a1a18;
  font: 16px/1.65 -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; }
main { max-width: 46rem; margin: 0 auto; }
h1 { font-size: 1.5rem; margin: 0 0 .25rem; letter-spacing: -0.01em; }
h2 { font-size: 1.05rem; margin: 2.5rem 0 .75rem; letter-spacing: -0.01em; }
.sub { color: #6b6b63; margin: 0 0 2rem; font-size: .9rem; }
.tally { display: flex; flex-wrap: wrap; gap: 1.5rem; padding: 1rem 1.25rem; border: 1px solid #e6e2da;
  border-radius: 10px; background: #fff; margin-bottom: 1.5rem; }
.tally div { font-size: .8rem; color: #6b6b63; }
.tally b { display: block; font-size: 1.5rem; color: #1a1a18; font-weight: 600; }
.caveat { border-left: 3px solid #d8d2c6; padding: .4rem 0 .4rem 1rem; color: #4a4a44;
  font-size: .875rem; margin: 1rem 0; }
.draft { border: 1px solid #e6e2da; border-radius: 10px; background: #fff; padding: 1.5rem;
  white-space: pre-wrap; font-size: .95rem; }
mark { padding: .05em .15em; border-radius: 3px; }
mark.exact, mark.exact-ellipsis { background: #d8f0dc; box-shadow: inset 0 -2px 0 #2f8f4e; }
mark.near { background: #fdf0d0; box-shadow: inset 0 -2px 0 #b8860b; }
mark.altered, mark.elsewhere { background: #fde3d6; box-shadow: inset 0 -2px 0 #c25a2a; }
mark.not-found { background: #fbd9d9; box-shadow: inset 0 -2px 0 #b32828; }
mark.unreadable { background: #eef0f6; box-shadow: inset 0 -2px 0 #3d4f8a; }
mark.not-checked { background: #eceae4; box-shadow: inset 0 -2px 0 #9a9a90; }
table { border-collapse: collapse; width: 100%; font-size: .85rem; }
th, td { text-align: left; padding: .5rem .6rem; border-bottom: 1px solid #ece8e0; vertical-align: top; }
th { font-weight: 600; color: #6b6b63; font-size: .75rem; text-transform: uppercase; letter-spacing: .04em; }
.wrap { overflow-x: auto; }
.q { color: #4a4a44; }
.diff ins { background: #d8f0dc; text-decoration: none; }
.diff del { background: #fbd9d9; }
footer { margin-top: 3rem; padding-top: 1.5rem; border-top: 1px solid #e6e2da; color: #6b6b63; font-size: .8rem; }
a { color: #3d4f8a; }
`;

export interface ReportInput {
  draftName: string;
  draftText: string;
  quotes: DraftQuote[];
  results: QuoteResult[];
  sources: ExtractedSource[];
  identifiers: IdentifierResult[];
  generatedAt: string;
}

export function buildReport(input: ReportInput): string {
  const { draftText, quotes, results, sources } = input;
  const byId = new Map(sources.map((s) => [s.id, s]));
  const resultFor = new Map(results.map((r) => [r.quoteId, r]));

  // Rebuild the draft with each quotation wrapped, walking the spans in order.
  const pieces: string[] = [];
  let cursor = 0;
  for (const quote of quotes) {
    if (quote.start < cursor) continue;
    const result = resultFor.get(quote.id);
    pieces.push(escapeHtml(draftText.slice(cursor, quote.start)));
    const cls = result?.cls ?? 'not-checked';
    const source = result?.sourceId != null ? byId.get(result.sourceId) : null;
    const where = source
      ? `${source.name}${result?.printedPage ? `, printed p. ${result.printedPage}` : result?.sheet ? `, sheet ${result.sheet}` : ''}`
      : 'no match';
    pieces.push(
      `<mark class="${cls}" title="${escapeHtml(`${CLASS_LABELS[cls]} — ${where}`)}">${escapeHtml(
        draftText.slice(quote.start, quote.end),
      )}</mark>`,
    );
    cursor = quote.end;
  }
  pieces.push(escapeHtml(draftText.slice(cursor)));

  const tally = {
    verified: results.filter((r) => r.cls === 'exact' || r.cls === 'exact-ellipsis').length,
    attention: results.filter((r) => ['near', 'altered', 'elsewhere', 'not-found'].includes(r.cls)).length,
    unchecked: results.filter((r) => r.cls === 'not-checked' || r.cls === 'unreadable').length,
  };

  const rows = results
    .map((result) => {
      const quote = quotes[result.quoteId];
      const source = result.sourceId != null ? byId.get(result.sourceId) : null;
      const diff = result.diff
        .filter((d) => d.kind !== 'same')
        .map((d) => {
          if (d.kind === 'insertion') return `<ins>${escapeHtml(d.quote ?? '')}</ins>`;
          if (d.kind === 'deletion') return `<del>${escapeHtml(d.source ?? '')}</del>`;
          return `<del>${escapeHtml(d.source ?? '')}</del> <ins>${escapeHtml(d.quote ?? '')}</ins>`;
        })
        .join(' · ');
      const page = result.printedPage
        ? `printed p. ${escapeHtml(result.printedPage)}`
        : result.sheet
          ? `sheet ${result.sheet}`
          : '';
      return `<tr>
        <td>${result.quoteId + 1}</td>
        <td>${escapeHtml(CLASS_LABELS[result.cls])}${result.orthographic ? ' <em>(spelling only)</em>' : ''}${
          result.pinpointMismatch ? '<br><strong>pinpoint differs</strong>' : ''
        }</td>
        <td>${escapeHtml(source?.name ?? '—')}<br><span class="q">${page}</span></td>
        <td class="q">${escapeHtml(quote?.text ?? '')}</td>
        <td class="diff">${diff || (result.skipped ? escapeHtml(result.skipped) : '')}${
          // The qualification travels with the verdict. This file is the thing a
          // supervisor or a reviewer actually reads, so a NOT FOUND that rests
          // on pages nobody could read must carry that fact here, not only on
          // the screen the author saw.
          result.blindPages ? `<br><strong>${escapeHtml(result.blindPages)}</strong>` : ''
        }</td>
      </tr>`;
    })
    .join('\n');

  // The bare enum member used to be printed here, so a run made on a train
  // reported every genuine DOI in the bibliography as "unresolvable". Each row
  // now says which of the two it is, and carries the reason with it.
  const identifierRows = input.identifiers
    .map((r) => {
      const note =
        r.verdict === 'not-checked'
          ? IDENTIFIER_UNREACHABLE_NOTE
          : r.verdict === 'rate-limited'
            ? IDENTIFIER_RATE_LIMITED_NOTE
            : '';
      return `<tr>
        <td>${escapeHtml(r.identifier.raw)}</td>
        <td>${escapeHtml(IDENTIFIER_VERDICT_LABELS[r.verdict])}${
          note ? `<br><span class="q">${escapeHtml(note)}</span>` : ''
        }</td>
        <td class="q">${escapeHtml(r.resolvedTitle ?? '—')}</td>
        <td class="q">${escapeHtml(r.registry ?? '—')}</td>
      </tr>`;
    })
    .join('\n');

  const unreachedIdentifiers = input.identifiers.filter((r) => r.verdict === 'not-checked').length;

  // Every note the screen shows about a file, in the file that travels. The
  // report used to print `pageNote` alone, which dropped the per-page "no text
  // layer" warning and replaced a real extraction error — an encrypted PDF, a
  // corrupt file — with a blanket "no text layer", sending the reader to OCR
  // instead of to the password.
  const preflight = sources
    .map(
      (s) => `<tr>
        <td>${escapeHtml(s.name)}</td>
        <td>${s.pages.length}</td>
        <td>${s.text.length.toLocaleString()}</td>
        <td class="q">${sourceNoteLines(s).map(escapeHtml).join('<br>')}</td>
      </tr>`,
    )
    .join('\n');

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Quotation audit — ${escapeHtml(input.draftName)}</title>
<style>${REPORT_CSS}</style></head>
<body><main>
<h1>Quotation audit</h1>
<p class="sub">${escapeHtml(input.draftName)} · checked against ${sources.length} source${
    sources.length === 1 ? '' : 's'
  } · ${escapeHtml(input.generatedAt)} · produced by quotewitness, entirely in the browser</p>

<div class="tally">
  <div><b>${results.length}</b>quotations checked</div>
  <div><b>${tally.verified}</b>verified</div>
  <div><b>${tally.attention}</b>need attention</div>
  <div><b>${tally.unchecked}</b>not checked</div>
</div>

<p class="caveat">${escapeHtml(EXACTNESS_CAVEAT)}</p>
<p class="caveat">${escapeHtml(NOT_FOUND_CAVEAT)}</p>

<h2>What was read out of each file</h2>
<div class="wrap"><table>
<thead><tr><th>File</th><th>Pages</th><th>Characters</th><th>What I can tell you about it</th></tr></thead>
<tbody>${preflight}</tbody></table></div>

<h2>Every quotation</h2>
<div class="wrap"><table>
<thead><tr><th>#</th><th>Verdict</th><th>Source</th><th>Quotation</th><th>Differences</th></tr></thead>
<tbody>${rows}</tbody></table></div>

${
  input.identifiers.length > 0
    ? `<h2>Identifiers</h2>
<p class="caveat">${escapeHtml(IDENTIFIER_TABLE_CAVEAT)}</p>${
        unreachedIdentifiers > 0
          ? `\n<p class="caveat">${escapeHtml(IDENTIFIER_UNREACHABLE_CAVEAT(unreachedIdentifiers))}</p>`
          : ''
      }
<div class="wrap"><table>
<thead><tr><th>Identifier</th><th>Verdict</th><th>Resolved title</th><th>Registry</th></tr></thead>
<tbody>${identifierRows}</tbody></table></div>`
    : ''
}

<h2>The draft</h2>
<div class="draft">${pieces.join('')}</div>

<footer>Generated by <a href="https://quotewitness.benrichardson.dev/">quotewitness</a>, which runs entirely in the browser — this report was assembled on the machine that produced it and no file was uploaded anywhere. It records whether words appear in files; it cannot tell you whether a quotation is honest.</footer>
</main></body></html>`;
}
